import { type FC, useCallback, useMemo } from 'react';

import { useNavigationContext } from '@/features/navigation/provider';
import { AerialMapPanel } from '@/features/selectionsPanel/exportMenu/aerialMap/aerialMapPanel';
import { SelectionsEmbedPanel } from '@/features/selectionsPanel/exportMenu/selectionsEmbedPanel';
import { TopLevelExportMenu } from '@/features/selectionsPanel/exportMenu/topLevelExportMenu';
import { PanelContentSwitcher } from '@/features/selectionsPanel/panelContentSwitcher';
import { SelectionsPanelContainer } from '@/features/selectionsPanel/selectionsPanelContainer';
import { SelectionsPanelHeader } from '@/features/selectionsPanel/selectionsPanelHeader';
import { useSelectionsPanelContext } from '@/features/selectionsPanel/selectionsPanelProvider';

const headerHeightInPx = 117;

export const SelectionsExportMenuPanel: FC = () => {
  const {
    isPanelOpen,
    setIsExportMenuOpen,
    isEmbedPanelOpen,
    setIsEmbedPanelOpen,
    isAerialReportPanelOpen,
    setIsAerialReportPanelOpen,
  } = useSelectionsPanelContext();

  const { aerialMapIsOpen } = useNavigationContext();

  const currentTitle = useMemo(() => {
    return isEmbedPanelOpen || aerialMapIsOpen ? 'Export' : 'Selections';
  }, [aerialMapIsOpen, isEmbedPanelOpen]);

  const goBack = useCallback(() => {
    if (isEmbedPanelOpen) {
      setIsEmbedPanelOpen(false);
      return;
    }

    if (isAerialReportPanelOpen) {
      setIsAerialReportPanelOpen(false);
      return;
    }

    setIsExportMenuOpen(false);
  }, [
    isAerialReportPanelOpen,
    isEmbedPanelOpen,
    setIsAerialReportPanelOpen,
    setIsEmbedPanelOpen,
    setIsExportMenuOpen,
  ]);

  const currentPanel = useMemo(() => {
    if (isAerialReportPanelOpen) return <AerialMapPanel />;
    if (isEmbedPanelOpen) return <SelectionsEmbedPanel />;
    return <TopLevelExportMenu />;
  }, [isAerialReportPanelOpen, isEmbedPanelOpen]);

  return (
    <SelectionsPanelContainer>
      <SelectionsPanelHeader
        headerHeightInPx={isPanelOpen ? headerHeightInPx : undefined}
      >
        <PanelContentSwitcher title={currentTitle} onClick={goBack} />
      </SelectionsPanelHeader>
      {isPanelOpen && (
        <div
          className="overflow-x-hidden overflow-y-scroll bg-gray-10 scrollbar-hide"
          style={{
            height: `calc(100vh - ${headerHeightInPx}px)`,
          }}
        >
          {currentPanel}
        </div>
      )}
    </SelectionsPanelContainer>
  );
};
